import { Button } from '../common/Button';
import { Logo } from '../common/Logo';
import type { MediaPermissionStatus } from '../../hooks/useMediaPermissions';

interface PermissionsPromptProps {
  status: MediaPermissionStatus;
  hasAudioDevice: boolean;
  hasVideoDevice: boolean;
  onRequest: () => void;
  onContinueWithoutMedia: () => void;
}

/**
 * Prompt shown when camera or microphone access is pending or denied.
 */
export function PermissionsPrompt({
  status,
  hasAudioDevice,
  hasVideoDevice,
  onRequest,
  onContinueWithoutMedia,
}: PermissionsPromptProps) {
  const content = getPromptContent(status, hasAudioDevice, hasVideoDevice);
  const missingDeviceNotice = getMissingDeviceNotice(hasAudioDevice, hasVideoDevice);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-bg-primary">
      <div className="card max-w-lg w-full p-8 text-center space-y-6">
        <div className="flex flex-col items-center gap-4">
          <Logo size="md" />
          <div>
            <h2 className="text-2xl font-semibold mb-2">{content.heading}</h2>
            <p className="text-gray-400">{content.message}</p>
          </div>
          {missingDeviceNotice && (
            <p className="text-sm text-yellow-500">{missingDeviceNotice}</p>
          )}
          <p className="text-sm text-gray-500">{content.helper}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {content.showEnableButton && (
            <Button variant="primary" onClick={onRequest}>
              {content.buttonLabel}
            </Button>
          )}
          <Button variant="ghost" onClick={onContinueWithoutMedia}>
            {content.continueLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

function getMissingDeviceNotice(
  hasAudio: boolean,
  hasVideo: boolean
): string | null {
  if (!hasAudio && !hasVideo) {
    return null;
  }
  if (!hasAudio) {
    return 'No microphone detected.';
  }
  if (!hasVideo) {
    return 'No camera detected.';
  }
  return null;
}

interface PromptContent {
  heading: string;
  message: string;
  helper: string;
  buttonLabel: string;
  continueLabel: string;
  showEnableButton: boolean;
}

function getPromptContent(
  status: MediaPermissionStatus,
  hasAudio: boolean,
  hasVideo: boolean
): PromptContent {
  if (status === 'no-devices') {
    return {
      heading: 'No Camera or Microphone Detected',
      message: 'No audio or video devices were found on this computer.',
      helper: 'Connect a microphone or camera and refresh, or continue without media.',
      buttonLabel: 'Retry Detection',
      continueLabel: 'Continue Without Media',
      showEnableButton: true,
    };
  }

  if (status === 'denied') {
    return getDeniedContent(hasAudio, hasVideo);
  }

  return getPromptStateContent(hasAudio, hasVideo);
}

function getDeniedContent(hasAudio: boolean, hasVideo: boolean): PromptContent {
  const deviceName = getDeviceName(hasAudio, hasVideo);

  return {
    heading: `${deviceName} Blocked`,
    message: `Access is blocked. Update browser permissions or continue without ${deviceName.toLowerCase()}.`,
    helper: 'Use the lock icon in the address bar to allow access, then try again.',
    buttonLabel: `Enable ${deviceName}`,
    continueLabel: `Continue Without ${deviceName}`,
    showEnableButton: true,
  };
}

function getPromptStateContent(hasAudio: boolean, hasVideo: boolean): PromptContent {
  const deviceName = getDeviceName(hasAudio, hasVideo);

  return {
    heading: `${deviceName} Access`,
    message: `Please allow access to your ${deviceName.toLowerCase()} to join the meeting.`,
    helper: 'If you do not see a prompt, check your browser site settings and try again.',
    buttonLabel: `Enable ${deviceName}`,
    continueLabel: `Continue Without ${deviceName}`,
    showEnableButton: true,
  };
}

function getDeviceName(hasAudio: boolean, hasVideo: boolean): string {
  if (hasAudio && hasVideo) {
    return 'Camera and Microphone';
  }
  if (hasAudio) {
    return 'Microphone';
  }
  if (hasVideo) {
    return 'Camera';
  }
  return 'Media Devices';
}
